import React, { useState, useRef, useEffect } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { cn } from '@/lib/utils';

export interface MagneticButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
  asChild?: boolean;
  className?: string;
}

export const MagneticButton = React.forwardRef<
  HTMLButtonElement,
  MagneticButtonProps
>(({ children, className, asChild = false, ...props }, ref) => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Check for prefers-reduced-motion
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setPrefersReducedMotion(mediaQuery.matches);

      const handleMotionChange = (e: MediaQueryListEvent) => {
        setPrefersReducedMotion(e.matches);
      };

      mediaQuery.addEventListener('change', handleMotionChange);

      // Check for touch device
      const touchQuery = window.matchMedia('(pointer: coarse)');
      setIsTouchDevice(touchQuery.matches);

      const handleTouchChange = (e: MediaQueryListEvent) => {
        setIsTouchDevice(e.matches);
      };

      touchQuery.addEventListener('change', handleTouchChange);

      return () => {
        mediaQuery.removeEventListener('change', handleMotionChange);
        touchQuery.removeEventListener('change', handleTouchChange);
      };
    }
  }, []);

  const mouseXSpring = useSpring(x, { stiffness: 300, damping: 30 });
  const mouseYSpring = useSpring(y, { stiffness: 300, damping: 30 });

  // Constrain movement to max 12px in any direction
  const constrainedX = useTransform(mouseXSpring, [-1, 1], [-12, 12]);
  const constrainedY = useTransform(mouseYSpring, [-1, 1], [-12, 12]);

  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (prefersReducedMotion || isTouchDevice || !buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Normalize to -1 to 1 range (center is 0)
    const xPct = (mouseX / width - 0.5) * 2;
    const yPct = (mouseY / height - 0.5) * 2;

    x.set(xPct);
    y.set(yPct);
  };

  const handleMouseLeave = () => {
    if (prefersReducedMotion || isTouchDevice) return;
    x.set(0);
    y.set(0);
  };

  const shouldUseMagnetic = !prefersReducedMotion && !isTouchDevice;

  return (
    <motion.button
      ref={(node) => {
        buttonRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
        }
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={
        shouldUseMagnetic
          ? {
              x: constrainedX,
              y: constrainedY,
            }
          : {}
      }
      className={cn(
        'bg-white text-[#0000FF] rounded-full px-8 py-3.5 md:px-10 md:py-3.5 font-bold',
        'transition-all duration-200 ease-out',
        'hover:shadow-lg hover:brightness-105',
        'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#0000FF] focus:ring-white',
        'active:scale-95',
        className
      )}
      whileTap={{ scale: 0.95 }}
      {...props}
    >
      {children}
    </motion.button>
  );
});

MagneticButton.displayName = 'MagneticButton';

